/**
 * Task 35: Dashboard 2.0 - Recruiter Productivity & Performance
 * Comprehensive analytics dashboard powered by analytics_events
 */

import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, Users, Phone, MessageSquare, FileText, 
  Filter, Download, Calendar, BarChart3, Award, Clock, Activity 
} from 'lucide-react';
import axiosInstance from '../config/axiosConfig';
import toast from '../utils/toast';

const DashboardV2 = () => {
  const [period, setPeriod] = useState('week');
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [jdAnalytics, setJdAnalytics] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);
  const [timeSeriesData, setTimeSeriesData] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [exportType, setExportType] = useState('summary');

  // Get current user info for RBAC checks
  const getCurrentUser = () => {
    try {
      const userStr = localStorage.getItem('spes_user');
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      return null;
    }
  };

  const currentUser = getCurrentUser();
  const canViewLeaderboard = currentUser && ['admin', 'tl'].includes(currentUser?.role);

  useEffect(() => {
    loadDashboardData();
  }, [period]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch all dashboard data in parallel
      const apiCalls = [
        axiosInstance.get(`/dashboard/v2/summary?period=${period}`),
        axiosInstance.get(`/dashboard/v2/funnel?period=${period}`),
        axiosInstance.get(`/dashboard/v2/jd-analytics?period=${period}`),
        axiosInstance.get(`/dashboard/v2/recent-events?limit=20`),
        axiosInstance.get(`/dashboard/v2/time-series?period=${period}`),
        axiosInstance.get(`/dashboard/v2/heatmap?period=${period}`)
      ];
      
      // Only fetch leaderboard if user has permission
      if (canViewLeaderboard) {
        apiCalls.push(
          axiosInstance.get(`/dashboard/v2/leaderboard?period=${period}&metric=productivity`).catch(() => ({ data: null }))
        );
      }
      
      const [summary, funnel, jdAnalyticsRes, events, timeSeries, heatmap, ...leaderboardResults] = await Promise.all(apiCalls);

      setSummaryData(summary.data);
      setFunnelData(funnel.data);
      setJdAnalytics(jdAnalyticsRes.data);
      setRecentEvents(events.data.events || []);
      setTimeSeriesData(timeSeries.data);
      setHeatmapData(heatmap.data);
      
      // Set leaderboard data only if fetched
      if (canViewLeaderboard && leaderboardResults.length > 0) {
        setLeaderboard(leaderboardResults[0].data);
      }
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (reportType) => {
    try {
      const response = await axiosInstance.get(
        `/dashboard/v2/export/csv?period=${period}&report_type=${reportType}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `dashboard_${reportType}_${period}_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export successful!');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed');
    }
  };

  const formatActionLabel = (action) => {
    const labels = {
      'candidate_added': 'Candidate Added',
      'call_initiated': 'Call Initiated',
      'call_completed': 'Call Completed',
      'whatsapp_sent': 'WhatsApp Sent',
      'log_added': 'Log Added'
    };
    return labels[action] || action;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard 2.0</h1>
        <p className="text-gray-600">Recruiter Productivity & Performance Analytics</p>
      </div>

      {/* Period Selector */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex flex-wrap gap-3 items-center justify-between">
        <div className="flex gap-2">
          {['today', 'week', 'month', 'year'].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                period === p
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>

        {/* Export Buttons */}
        <div className="flex gap-2 items-center">
          <select 
            value={exportType} 
            onChange={(e) => setExportType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="summary">Summary Report</option>
            <option value="events">Event Log</option>
            <option value="jd_analytics">JD Analytics</option>
          </select>
          <button
            onClick={() => handleExport(exportType)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Download size={18} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summaryData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <SummaryCard
            title="Candidates Added"
            value={summaryData.metrics.candidates_added}
            icon={<Users className="text-blue-600" size={24} />}
            color="blue"
          />
          <SummaryCard
            title="Calls Made"
            value={summaryData.metrics.calls_made}
            icon={<Phone className="text-green-600" size={24} />}
            color="green"
          />
          <SummaryCard
            title="WhatsApp Sent"
            value={summaryData.metrics.whatsapp_sent}
            icon={<MessageSquare className="text-purple-600" size={24} />}
            color="purple"
          />
          <SummaryCard
            title="Logs Added"
            value={summaryData.metrics.logs_added}
            icon={<FileText className="text-orange-600" size={24} />}
            color="orange"
          />
          <SummaryCard
            title="Follow-ups Done"
            value={summaryData.metrics.followups_done}
            icon={<Clock className="text-red-600" size={24} />}
            color="red"
          />
        </div>
      )}

      {/* Funnel Visualization */}
      {funnelData && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <TrendingUp className="mr-2 text-blue-600" size={24} />
            Recruitment Funnel
          </h2>
          <div className="space-y-3">
            {funnelData.funnel.map((stage, idx) => (
              <div key={idx} className="flex items-center">
                <div className="w-32 text-sm font-semibold text-gray-700">{stage.stage}</div>
                <div className="flex-1 flex items-center gap-2">
                  <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-blue-600 h-full rounded-full flex items-center justify-center text-white text-xs font-bold transition-all"
                      style={{ width: `${stage.conversion}%` }}
                    >
                      {stage.count > 0 && <span>{stage.count}</span>}
                    </div>
                  </div>
                  <div className="w-16 text-sm font-semibold text-gray-600">
                    {stage.conversion.toFixed(1)}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Leaderboard (R9: RBAC Guard) */}
        {canViewLeaderboard && leaderboard && leaderboard.leaderboard && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Award className="mr-2 text-yellow-600" size={24} />
              Leaderboard (Productivity)
            </h2>
            <div className="space-y-2">
              {leaderboard.leaderboard.slice(0, 10).map((recruiter) => (
                <div
                  key={recruiter.rank}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-yellow-100 text-yellow-800 font-bold text-sm">
                      {recruiter.rank}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{recruiter.name}</p>
                      <p className="text-xs text-gray-500">{recruiter.role}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">{recruiter.score}</p>
                    <p className="text-xs text-gray-500">points</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* JD Analytics */}
        {jdAnalytics && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <BarChart3 className="mr-2 text-green-600" size={24} />
              JD-wise Analytics
            </h2>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {jdAnalytics.jd_analytics.slice(0, 10).map((jd) => (
                <div
                  key={jd.jd_id}
                  className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{jd.role_title}</p>
                      <p className="text-xs text-gray-500">{jd.client_name}</p>
                    </div>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                      {jd.conversion_rate.toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex gap-4 text-xs text-gray-600">
                    <span>👤 {jd.candidates_added} candidates</span>
                    <span>📞 {jd.calls_made} calls</span>
                    <span>📝 {jd.logs_added} logs</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Recent Events Feed */}
      {recentEvents.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <Activity className="mr-2 text-purple-600" size={24} />
            Recent Activity
          </h2>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {recentEvents.map((event) => (
              <div
                key={event.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
              >
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">
                      {formatActionLabel(event.action)}
                    </p>
                    <p className="text-xs text-gray-600">
                      {event.recruiter_name} 
                      {event.candidate_name && ` → ${event.candidate_name}`}
                      {event.channel && ` via ${event.channel}`}
                    </p>
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  {new Date(event.timestamp).toLocaleTimeString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Time Series Table (R7: Table Fallback) */}
      {timeSeriesData && timeSeriesData.status === 'ok' && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <TrendingUp className="mr-2 text-blue-600" size={24} />
            Time Series Data ({period})
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Candidates</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Calls</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Logs</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {timeSeriesData.time_series?.slice(0, 10).map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.date}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.candidates_added || 0}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.calls_made || 0}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.logs_added || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Heatmap Table (R7: Table Fallback) */}
      {heatmapData && heatmapData.status === 'ok' && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <Calendar className="mr-2 text-orange-600" size={24} />
            Activity Heatmap ({period})
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Day</th>
                  {heatmapData.labels?.hours?.slice(0, 12).map((hour) => (
                    <th key={hour} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{hour}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {heatmapData.heatmap?.slice(0, 7).map((row, dayIndex) => (
                  <tr key={dayIndex}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {heatmapData.labels?.days?.[dayIndex] || `Day ${dayIndex}`}
                    </td>
                    {row.slice(0, 12).map((count, hourIndex) => (
                      <td key={hourIndex} className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className={`text-center px-2 py-1 rounded ${count > 0 ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-500'}`}>
                          {count}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Summary Card Component
const SummaryCard = ({ title, value, icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200',
    green: 'bg-green-50 border-green-200',
    purple: 'bg-purple-50 border-purple-200',
    orange: 'bg-orange-50 border-orange-200',
    red: 'bg-red-50 border-red-200'
  };

  return (
    <div className={`${colorClasses[color]} border rounded-lg p-4`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-gray-700">{title}</span>
        {icon}
      </div>
      <p className="text-3xl font-bold text-gray-900">{value || 0}</p>
    </div>
  );
};

export default DashboardV2;
